---
title: "The eBay API in 2026: what the official one gives you, and how to get sold prices"
description: "eBay's free API covers live listings, and its sold-price API is for approved partners. We ran 60 calls and priced 7 options to get eBay sold data from code."
date: "2026-10-24"
postType: "alternatives"
author: "Kevin Wang"
tags: ["ebay", "ecommerce", "sold-listings", "api", "comparison"]
keyword: "ebay api"
ogImage: "/og/ebay-api.png"
---

export const faqItems = [
  {
    q: "Does eBay have a public API?",
    a: "Yes, for live listings. The Browse API searches items for sale and returns item details. eBay doesn't mark it as a limited release, and its default limit is 5,000 calls a day once you have a developer keyset (eBay's API call limits page, October 2, 2026). It has no filter for sold or ended listings.",
  },
  {
    q: "Can I get eBay sold prices from the official API?",
    a: "Not as an ordinary developer. eBay's sold-item search lives in the Marketplace Insights API, whose docs sit behind a developer sign-in, and eBay's Buy API requirements say production use of the Buy APIs is intended for eBay partners only, with no guarantee of approval. The older Finding API was decommissioned on February 4, 2025.",
  },
  {
    q: "How much does an eBay sold listings API cost?",
    a: ["For 10,000 sold-listing searches a month, prices on October 2, 2026 were $29 at SoldComps (Growth plan, up to 240 sales a search) and ", { sku: "ebay.sold_listings", calls: 10000 }, " at AnyAPI (up to 60 sales a call, billed per call). Apify's eBay Sold Listings Search bills per sale instead: $4.00 per 1,000 on Apify's Free plan."],
  },
  {
    q: "Is an eBay sold price what the buyer actually paid?",
    a: "Not when the sale was a Best Offer. eBay doesn't publish the accepted offer, so a sold-listings API returns the asking price for those sales. On October 2, 2026, 244 of 1,200 sales returned across 20 searches were marked Best Offer accepted.",
  },
];

<Callout type="tip" title="Key findings">

- **For live listings, use eBay's own [Browse API](https://developer.ebay.com/develop/api/buy/browse_api).** It's free with an eBay developer keyset, the default limit is 5,000 calls a day, and it only returns items that are still for sale.
- **eBay's sold-price API isn't open to ordinary developers.** Sold-item search is in the Marketplace Insights API, whose docs need a developer sign-in, and eBay says production use of its Buy APIs "is intended for eBay partners only".
- **To get sold prices from code without approval, use a hosted sold-listings API.** [AnyAPI](https://getanyapi.com/api/ebay/sold-listings) returned 60 sales on all 20 searches tested, in a median of about 2 seconds. [SoldComps](https://sold-comps.com/) is a flat $29 a month for 10,000 searches.
- **About 1 in 5 sales was a Best Offer, and eBay doesn't show what the buyer paid.** Filter those out, or mark them, before you average anything.

</Callout>

A storage-unit haul, a shelf of film cameras, an app that suggests a list price: they all need one number. What sellers are asking on eBay today doesn't help much. You need what the thing sold for last week.

eBay has an API, and it's free. It just doesn't give you that number. I read eBay's developer docs to see where the official route stops, sent 20 product searches through AnyAPI's three eBay endpoints on October 2, 2026, and priced the rest from their own pages.

Here's what each option is for.

## The best eBay APIs

- [eBay Browse API](#best-ebay-api-for-live-listings-at-no-cost) for live listings at no cost
- [eBay Marketplace Insights API](#best-ebay-api-for-sold-prices-with-ebays-approval) for sold prices with eBay's approval
- [Terapeak Product Research](#best-ebay-api-for-pricing-items-by-hand) for pricing items by hand
- [AnyAPI](#best-ebay-api-for-sold-prices-and-live-listings-on-one-key) for sold prices and live listings on one key
- [SoldComps](#best-ebay-api-for-sold-prices-on-a-flat-monthly-plan) for sold prices on a flat monthly plan
- [Apify eBay Sold Listings Search](#best-ebay-api-for-batch-exports-across-eight-ebay-sites) for batch exports across eight eBay sites
- [SerpApi eBay Search API](#best-ebay-api-for-live-ebay-results-next-to-google) for live eBay results next to Google

## What makes a good eBay API?

I'm assuming you're building something that prices used goods: a reseller tool, a comps feature, an estate-sale spreadsheet. You need sold prices for a keyword, and sometimes the live listings and one item's details next to them. Here's what I compared.

- **Sold prices.** An API that only sees live listings tells you what sellers hope to get.
- **Access without an application.** A route that needs a partner agreement is fine for a marketplace company and useless for a weekend project.
- **The fields a comp needs.** Sold price, sale date, condition, shipping, and whether the sale was a Best Offer, on every row.
- **Price at your volume.** Every price below is for 10,000 searches a month, on monthly billing.

<Callout type="note" title="How this was tested, and its limits">

- AnyAPI: 20 searches, from "nintendo switch oled" to "nikon z6 ii", each sent once to `ebay.sold_listings` (limit 60) and once to `ebay.search` (limit 25), then the first search result for each went to `ebay.product`. That's 60 calls, one at a time from one machine, on 2026-10-02.
- Sold prices weren't checked against eBay's own sold page, and AnyAPI's filters and paging weren't tested. Speeds are from 20 calls per endpoint, so they're a rough guide, not a benchmark.
- eBay's APIs weren't called. The Browse API needs a developer keyset, and Marketplace Insights needs eBay's approval, so their sections are read from eBay's developer docs on 2026-10-02.
- SoldComps, Apify, and SerpApi weren't run. Their sections are from their own pages on 2026-10-02.
- Prices are for 10,000 searches a month on monthly billing, checked on 2026-10-02.

</Callout>

## The best eBay APIs at a glance

| Tool | Best for | Why pick it | Pricing at 10,000 searches a month |
| --- | --- | --- | --- |
| eBay Browse API | Live listings at no cost | eBay's own data, with filters by category, condition, price, and GTIN | $0; default limit 5,000 calls a day |
| eBay Marketplace Insights API | Sold prices with eBay's approval | eBay's own sold-item search | No published price; production access by application |
| Terapeak Product Research | Pricing items by hand | Three years of eBay sales data in Seller Hub | Not an API; part of Seller Hub |
| AnyAPI | Sold prices and live listings on one key | Sold, live, and item-detail endpoints, billed per call | <Price sku="ebay.sold_listings" calls={10000} /> for sold searches (<Price sku="ebay.sold_listings" /> per 1,000) |
| SoldComps | Sold prices on a flat monthly plan | Up to 240 sales a search, eight eBay sites | $29 (Growth plan, 10,000 requests) |
| Apify eBay Sold Listings Search | Batch exports across eight eBay sites | Runs on Apify, on a schedule, into a dataset | $4.00 per 1,000 sales on the Free plan, $2.50 on Business |
| SerpApi eBay Search API | Live eBay results next to Google | One key for eBay and other search engines | $150 (Production plan, 15,000 searches) |

## Best eBay API for live listings at no cost

### eBay Browse API

<Figure src="/og/screenshots/ebay-api-ebay-filters-lastsolddate.png" alt="eBay's Buy API Field Filters page, the row for lastSoldDate: supported by Marketplace Insights search only, with the description Only listings ended within the date range period are returned" caption="eBay's Buy API filter reference on October 2, 2026: the one sold-date filter, lastSoldDate, belongs to Marketplace Insights, not Browse." width={1280} height={900} />

**eBay Browse API pros:**

- eBay's own data, and eBay doesn't mark it as a limited release
- No charge, with no third party between you and the listing
- Search by keyword, category, GTIN, eBay product ID, or image, with filters for condition, price, and delivery

**eBay Browse API cons:**

- Live listings only: its end-date filter returns items "scheduled to end" in a range, and it has no sold filter
- One search can page through at most 10,000 items
- You register an app and handle OAuth tokens before the first call

If you need what's for sale on eBay right now, start here. You join the eBay Developers Program, create an app, and swap its keys for an application access token with the client credentials flow. Then `GET /item_summary/search` takes a keyword and filters, and `GET /item/{item_id}` returns one listing.

eBay's [call limits page](https://developer.ebay.com/develop/api/buy/api_call_limits) gives Browse 5,000 calls a day by default, and says that once you have a keyset "you can start using eBay APIs immediately". eBay's [Buy API overview](https://developer.ebay.com/api-docs/buy/static/buy-overview.html) marks several Buy APIs as Limited Release, and Browse isn't one of them. For a price tracker on a few hundred items, that's plenty.

**What it won't do is tell you what anything sold for.** The [filter reference](https://developer.ebay.com/api-docs/buy/static/ref-buy-browse-filters.html) lists one sold-date filter, `lastSoldDate`, and it's marked for Marketplace Insights only. Browse's own date filter, `itemEndDate`, returns "items scheduled to end" in a range, which means live ones.

If a tutorial points you to the Finding API for completed items, it's out of date. eBay's [deprecation table](https://developer.ebay.com/develop/get-started/api-deprecation-status) shows the Finding API decommissioned on February 4, 2025, "replaced by the Browse API".

**eBay Browse API pricing:** no charge. A default of 5,000 calls a day, with higher limits after eBay's Application Growth Check. Source: [API call limits](https://developer.ebay.com/develop/api/buy/api_call_limits), October 2, 2026.

## Best eBay API for sold prices with eBay's approval

### eBay Marketplace Insights API

<Figure src="/og/screenshots/ebay-api-ebay-buy-requirements.png" alt="eBay's Buy APIs Requirements page: Many of the Buy APIs are a Limited Release. The use of eBay's Buy APIs in production is intended for eBay partners only. You must apply for production access through the eBay Partner Network. Note: There is no guarantee that your application for production use of the APIs will be approved" caption="eBay's Buy API requirements on October 2, 2026: production use is for eBay partners, by application, with no guarantee." width={1280} height={900} />

**eBay Marketplace Insights API pros:**

- eBay's own sold-item search, with a `lastSoldDate` filter for a date range
- The data comes from eBay, so there's no page to parse and nothing to break when eBay redesigns

**eBay Marketplace Insights API cons:**

- Its docs need an eBay developer sign-in, and it isn't on eBay's public list of Buy APIs
- Production access is an application to the eBay Partner Network, and approval isn't guaranteed
- eBay's license needs its written permission before you derive an average selling price for a category

This is eBay's sold-price API, and it's the one you probably can't have. On October 2, 2026, its overview and search-method pages redirected to eBay's developer sign-in page, under a path named `marketplace-insights-private`, and eBay's public overview of the Buy APIs doesn't list it.

The way in is the Buy API process. eBay's [Buy API requirements](https://developer.ebay.com/api-docs/buy/static/buy-requirements.html) say production use "is intended for eBay partners only". You apply through the eBay Partner Network with mocks and data flows of your product, eBay says it answers within 10 business days, and acceptance "is based on the proposed business model".

Even with access, check what you're allowed to build. eBay's [API License Agreement](https://developer.ebay.com/join/api-license-agreement) says you need its "express prior written permission" to use eBay content in a way that derives the "average selling price or gross merchandise sold for any eBay category".

If you run a marketplace or a pricing product that eBay would want as a partner, apply. If you're one developer with a reseller tool, plan on another route.

**eBay Marketplace Insights API pricing:** no published price. Access is by application through the eBay Partner Network. Source: [Buy API requirements](https://developer.ebay.com/api-docs/buy/static/buy-requirements.html), October 2, 2026.

## Best eBay API for pricing items by hand

### Terapeak Product Research

<Figure src="/og/screenshots/ebay-api-ebay-product-research.png" alt="eBay's Product research help page: Product research gives you access to the last 3 years of eBay sales data for millions of items, including sales trends, average sales price, sold price range, average shipping costs, sell through rate, total number of sellers, and the selling format" caption="eBay's Product research help page on October 2, 2026: three years of sales data, inside Seller Hub." width={1280} height={900} />

**Terapeak Product Research pros:**

- Three years of eBay sales data, where SoldComps and the Apify actor go back 90 days
- Average sold price, price range, sell-through rate, and shipping, already worked out
- Sellers with Seller Hub get it, with no developer account

**Terapeak Product Research cons:**

- A research screen you search by hand
- Sourcing insights, the inventory side, needs a Basic Store subscription or above

If you're pricing a few dozen items yourself, you may not need an API. eBay's [Product research](https://www.ebay.com/help/selling/selling-tools/terapeak-research?id=4853) tool, at a help address that still says Terapeak, sits in the Research tab of Seller Hub. eBay's help page says it covers "the last 3 years of eBay sales data for millions of items".

It's the deepest sold history on this page. It's also a screen you search by hand, so it doesn't help an app that prices items for its users.

**Terapeak Product Research pricing:** part of Seller Hub for sellers who have it; Sourcing insights is included with a Basic Store subscription or above. Source: [eBay help](https://www.ebay.com/help/selling/selling-tools/terapeak-research?id=4853), October 2, 2026.

## Best eBay API for sold prices and live listings on one key

### AnyAPI

<Figure src="/og/charts/ebay-api-sold-recency.svg" alt="Bar chart of 20 eBay searches, showing how many of the 60 sales returned per search sold on October 1 or 2, 2026, and how many in the last 7 days. Nintendo Switch OLED 59 in 2 days and 60 in 7; iPhone 13 Pro 128GB 58 and 59; Vintage Levi's 501 57 and 57; PS5 console 56 and 56; AirPods Pro 2 55 and 56; Meta Quest 3 54 and 54; KitchenAid mixer 53 and 55; RTX 4090 53 and 54; Charizard PSA 10 52 and 53; MacBook Air M2 52 and 53; Canon AE-1 camera 48 and 54; Steam Deck OLED 39 and 60; Jordan 1 Chicago 39 and 58; Yeti 30 oz tumbler 29 and 56; Dyson V15 23 and 59; Rolex Submariner 22 and 58; GoPro Hero 12 18 and 54; Apple Watch Series 9 15 and 15; LEGO 75192 Falcon 2 and 20; Nikon Z6 II 1 and 11" caption="One sold-listings call per search, 60 sales each, October 2, 2026 (UTC). For half the searches, 50 or more of the 60 sales were from the last two days." width={760} height={950} />

**AnyAPI pros:**

- 60 sales on all 20 searches, with sale date, condition, seller, and listing type on all 1,200, and a sold price on 1,198
- A `bestOfferAccepted` flag, so you can set aside sales where the price shown isn't the price paid
- Sold listings, live search, and item details on one key, billed per call from a prepaid wallet

**AnyAPI cons:**

- Sold listings cover ebay.com only
- 60 sales per call, so a full week of a popular item means walking several pages

[AnyAPI](https://getanyapi.com/api/ebay) is the pick if your product needs sold prices from code and you don't want a plan. You send a keyword to `ebay.sold_listings`, and the sales come back as the same JSON whatever you search for.

```bash
curl -sS -X POST 'https://api.getanyapi.com/v1/run/ebay.sold_listings' \
  -H "Authorization: Bearer $ANYAPI_API_KEY" \
  -H 'Content-Type: application/json' \
  -d '{"query":"vintage levis 501","limit":60}'
```

Here's one of the 60 sales it returned on 2026-10-02, trimmed, with the rest of the envelope:

```json
{
  "output": {
    "found": true,
    "data": {
      "hasMore": true,
      "items": [
        {
          "itemId": "800679023504",
          "title": "Vintage 90s Levi's 501 Made In USA Black Button Fly Jeans 36x30",
          "url": "https://www.ebay.com/itm/800679023504",
          "soldPrice": 79.99,
          "soldCurrency": "USD",
          "soldUtc": 1790899200,
          "shippingCost": "$10.15",
          "condition": "Pre-Owned",
          "listingType": "Fixed price",
          "bestOfferAccepted": true
        }
      ]
    }
  },
  "costUsd": 0.003,
  "items": 60
}
```

All 20 sold-listings calls came back with 60 sales in a median of 1.9 seconds, and none took longer than about 3. Every one was charged the lowest published price, and every search had more pages behind it. {/* receipt 2026-10-02 */}

| Endpoint | Calls on 2026-10-02 | What came back | Median time |
| --- | --- | --- | --- |
| `ebay.sold_listings` | 20, limit 60 | 60 sales on all 20, with more pages on all 20 | 1.9 s |
| `ebay.search` | 20, limit 25 | 25 live listings on all 20 | 5.1 s |
| `ebay.product` | 20, one per search | The listing on all 20, with brand on 19 and watcher count on 16 | 3.7 s |

**How far back 60 sales reach depends on the item, so plan your polling around it.** For 10 of the 20 searches, at least 50 of the 60 sales were from October 1 or 2. For Nikon's Z6 II, only 11 were from the last week. A daily job on a popular item should walk pages until it reaches sales it already has. The sale date has no time of day.

**Best Offers are the other thing to handle before you average.** 244 of the 1,200 sales were marked `bestOfferAccepted`, and for those, `soldPrice` is the asking price, because eBay doesn't publish the accepted offer. Vintage Levi's 501s had 27 of 60. Condition matters too: 677 of the sales were pre-owned, 255 brand new, and 58 for parts, and `condition` takes `new` or `used` as a filter.

The same key does live search with [`ebay.search`](https://getanyapi.com/api/ebay/search) and one listing's details with [`ebay.product`](https://getanyapi.com/api/ebay/product), including watchers, item location, whether returns are accepted, and quantity sold where the listing shows it. For a reseller tool, that's comps, competing listings, and the listing you're about to undercut, from one wallet.

**AnyAPI pricing:** <Price sku="ebay.sold_listings" /> per 1,000 sold-listings calls, up to 60 sales each, which is <Price sku="ebay.sold_listings" items={60} /> per 1,000 sales at a full page. Live search and item details are <Price sku="ebay.search" /> and <Price sku="ebay.product" /> per 1,000. A call the cheapest source can't answer goes to a backup, which costs up to <Price sku="ebay.sold_listings" failover /> per 1,000 for sold listings and <Price sku="ebay.search" failover /> for search. Prepaid, no plan; new accounts get free credit, no card required.

## Best eBay API for sold prices on a flat monthly plan

### SoldComps

<Figure src="/og/screenshots/ebay-api-soldcomps-pricing.png" alt="SoldComps pricing: Pay per search, not per result. One request is one keyword search, up to 240 real sold listings. Basic 100 requests a month for $0, no credit card; Starter 2,000 for $9 a month; Growth 10,000 for $29 a month; Scale 50,000 for $79 a month; every plan 60 requests a minute, 8 eBay marketplaces, up to 90 days of history" caption="SoldComps' plans on October 2, 2026: $29 a month for 10,000 searches, each returning up to 240 sales." width={1280} height={900} />

**SoldComps pros:**

- Up to 240 sales per request, four times a page from AnyAPI
- Eight eBay sites: US, UK, Germany, France, Italy, Spain, Canada, and Australia
- 100 free requests a month, no card

**SoldComps cons:**

- Up to 90 days of history per search
- A monthly quota: past it, requests are blocked until the next cycle unless you upgrade

[SoldComps](https://sold-comps.com/) does one thing: you send a keyword in a GET request, and it returns up to 240 completed eBay sales with price, shipping, date, condition, and seller. It counts a request the same whether it returns 10 sales or 240.

At a steady 10,000 searches a month, the $29 Growth plan costs about the same as AnyAPI's per-call price, and each search can carry four times as many sales. If you need sales from eBay UK or Germany, it's the only sold-listings option here that's priced per search and says it covers them.

Skip it if your volume is uneven. The quota resets monthly, and a month you don't use is still $29.

**SoldComps pricing:** Basic is free for 100 requests a month. Starter is $9 for 2,000, Growth $29 for 10,000, and Scale $79 for 50,000, each with up to 90 days of history. Source: [sold-comps.com](https://sold-comps.com/), October 2, 2026.

## Best eBay API for batch exports across eight eBay sites

### Apify eBay Sold Listings Search

<Figure src="/og/screenshots/ebay-api-apify-pricing.png" alt="The Apify Store pricing tab for eBay Sold Listings Search by Caffein Dev: Result, a single result in the default dataset, $4.00 per 1,000 on the Free plan and $2.50 per 1,000 on Business with a 37% Gold discount; Actor start $0.00005; platform usage included. 3.6K total users and 800 monthly active users" caption="The actor's Apify Store pricing on October 2, 2026: $4.00 per 1,000 sales on Apify's Free plan, $2.50 on Business." width={1280} height={900} />

**Apify eBay Sold Listings Search pros:**

- Up to six keywords per run, each with the same filters, on eight eBay sites
- A date window of 1 to 90 days back, and Best Offer detection on by default
- Runs on Apify, so it can run on a schedule and write to an Apify dataset

**Apify eBay Sold Listings Search cons:**

- Billed per sale, so a search that returns 60 sales costs 60 times more than one that returns 1
- A run is a batch job that writes to a dataset, so your code waits for the run to finish

Caffein Dev's [eBay Sold Listings Search](https://apify.com/caffein.dev/ebay-sold-listings) is an Apify actor: you start a run with a list of keywords and filters, and it writes each sale to a dataset. Apify's store showed about 3,600 total users and 800 monthly active users on October 2, 2026.

It suits a weekly export of comps for a few hundred keywords into a spreadsheet or warehouse. At volume, the per-sale billing adds up: at $4.00 per 1,000 sales, a search that returns 60 sales costs $0.24 on the Free plan.

**Apify eBay Sold Listings Search pricing:** $4.00 per 1,000 sales on Apify's Free plan and $2.50 on Business, plus $0.00005 per run start. Source: [Apify Store](https://apify.com/caffein.dev/ebay-sold-listings), October 2, 2026.

## Best eBay API for live eBay results next to Google

### SerpApi eBay Search API

<Figure src="/og/screenshots/ebay-api-serpapi-sold-deprecated.png" alt="SerpApi's eBay Search API docs, the show_only parameter: available options are free returns, returns accepted, authorized seller, deals and savings, sale items, lots, charity, authenticity guarantee, free shipping, and local pickup. A note reads: The Sold and Complete options are deprecated and no longer supported" caption="SerpApi's eBay Search API docs on October 2, 2026: the Sold and Complete filters are deprecated." width={1280} height={900} />

**SerpApi eBay Search API pros:**

- eBay search, product, and seller APIs on the same key as Google and other engines
- A repeated search within an hour is served from cache and isn't counted
- 250 free searches a month

**SerpApi eBay Search API cons:**

- Live listings only: its docs say the Sold and Complete options "are deprecated and no longer supported"
- Priced by the search across a monthly plan, so 10,000 eBay searches need the $150 plan

[SerpApi](https://serpapi.com/ebay-search-api) returns eBay's search page as JSON, with filters for price, condition, buying format, and seller. If you already pay SerpApi for Google results and want eBay's live listings beside them, it's one more engine on a key you have.

It won't get you sold prices. The `show_only` parameter used to take Sold and Complete, and the docs now say both are deprecated.

**SerpApi eBay Search API pricing:** 250 free searches a month. Starter is $25 a month for 1,000 searches, Developer $75 for 5,000, and Production $150 for 15,000. Source: [SerpApi pricing](https://serpapi.com/pricing), October 2, 2026.

## Which eBay API should you use?

- You need live listings and can register an eBay app: the eBay Browse API.
- You're a marketplace or pricing company eBay would partner with: apply for Marketplace Insights.
- You're pricing items yourself, a few at a time: Terapeak Product Research in Seller Hub.
- You need sold prices from code, pay as you go, plus live search and item details: AnyAPI.
- You run a steady 10,000 sold searches a month, or need eBay UK or Germany: SoldComps.
- You want a scheduled export of comps into a dataset: Apify's eBay Sold Listings Search.
- You already use SerpApi for Google and want eBay's live results too: SerpApi.

Whichever you pick, check the Best Offer flag before you average a sold price. The [eBay API page](/api/ebay) lists all of AnyAPI's eBay endpoints, and the [Amazon API guide](/blog/amazon-api-for-web-scraping) covers the other half of most resellers' price research.

<FAQ items={faqItems} />

<CTA to="/api/ebay/sold-listings" note={<>eBay sold prices as clean JSON, <Price sku="ebay.sold_listings" per="call" /> for up to 60 sales, no plan and nothing that expires. New accounts get free credit, no card required.</>}>
  Try the eBay Sold Listings API
</CTA>

Use data responsibly and follow AnyAPI's [Acceptable Use Policy](/acceptable-use).
